import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useGlobalStore } from "~/stores/store-provider";
import { Connection } from "~/stores/slices/database";
import DeleteConnectionModal from "~/components/connections/delete-connection-modal";
import {
  FaArrowLeft,
  FaCheck,
  FaTimes,
  FaTrash,
  FaInfoCircle,
  FaDatabase,
  FaUser,
  FaLock,
  FaServer,
  FaNetworkWired,
} from "react-icons/fa";
import { SiPostgresql } from "react-icons/si";

type EditPostgreSQLConnectionProps = {
  connection: Connection;
};

type PostgreSQLFormData = {
  name: string;
  username: string;
  password: string;
  host: string;
  port: number;
  database: string;
};

const EditPostgreSQLConnection = ({
  connection,
}: EditPostgreSQLConnectionProps) => {
  const navigate = useNavigate();
  const { connectionDetails, updateConnection, deleteConnection } =
    useGlobalStore((state) => state);
  const timersRef = useRef<Set<NodeJS.Timeout>>(new Set());

  const [formData, setFormData] = useState<PostgreSQLFormData>({
    name: "",
    username: "",
    password: "",
    host: "",
    port: 5432,
    database: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof PostgreSQLFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");

  useEffect(() => {
    // Initialize form data from connection details
    if (connectionDetails) {
      setFormData({
        name: connection.name,
        username: connectionDetails.username || "",
        password: "", // Password needs to be re-entered for security
        host: connectionDetails.host || "",
        port: connectionDetails.port || 5432,
        database: connectionDetails.database || "",
      });
    }
  }, [connectionDetails, connection.name]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
    };
  }, []);

  const handleInputChange = (
    field: keyof PostgreSQLFormData,
    value: string | number
  ) => {
    setFormData({
      ...formData,
      [field]: field === "port" ? Number(value) : value,
    });

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof PostgreSQLFormData, string>> = {};

    // Validate connection name
    if (!formData.name.trim()) {
      newErrors.name = "Connection name is required";
    } else if (formData.name.length < 3 || formData.name.length > 50) {
      newErrors.name = "Must be between 3 and 50 characters";
    }

    // Validate username
    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    }

    // Validate host
    if (!formData.host.trim()) {
      newErrors.host = "Host is required";
    }

    // Validate port
    if (formData.port <= 0 || formData.port > 65535) {
      newErrors.port = "Port must be between 1 and 65535";
    }

    // Validate database
    if (!formData.database.trim()) {
      newErrors.database = "Database name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare update data matching the API expected format (same as postgress-edit-modal.tsx)
      const updateData = {
        type: "postgresql", // Use 'type' instead of 'connection_type'
        username: formData.username,
        password: formData.password,
        host: formData.host,
        port: formData.port,
        database: formData.database,
        connectionName: formData.name, // Use 'connectionName' for the name field
        attachments: [], // Add empty attachments array as required by API
      };

      await updateConnection(connection._id, updateData);

      // Show success toast and navigate
      setToastType("success");
      setToastMessage("Connection updated successfully!");
      setShowToast(true);

      const timer = setTimeout(() => {
        navigate("/connections");
        timersRef.current.delete(timer);
      }, 1500);
      timersRef.current.add(timer);
    } catch (error) {
      console.error("Failed to update connection:", error);

      // Show error toast
      setToastType("error");
      setToastMessage("Failed to update connection. Please try again.");
      setShowToast(true);

      const timer = setTimeout(() => {
        setShowToast(false);
        timersRef.current.delete(timer);
      }, 2000);
      timersRef.current.add(timer);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteConnection(connection._id);
      navigate("/connections");
    } catch (error) {
      console.error("Failed to delete connection:", error);
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
    }
  };

  return (
    <div className="h-full bg-base-200/50 overflow-y-auto">
      <div className="container mx-auto px-4 py-8 max-w-2xl min-h-full">
        {/* Back Navigation */}
        <button
          onClick={() => navigate("/connections")}
          className="btn btn-ghost btn-sm gap-2 mb-6"
        >
          <FaArrowLeft className="w-3 h-3" />
          Back to Connections
        </button>

        {/* Main Card */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body p-8">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-base-200">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <SiPostgresql className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Edit PostgreSQL Connection</h1>
                <p className="text-sm text-base-content/60">Update your database settings</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Connection Name */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">Connection Name</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="e.g., Production DB"
                  className={`input input-bordered w-full ${
                    errors.name ? "input-error" : ""
                  }`}
                />
                {errors.name && (
                  <label className="label">
                    <span className="label-text-alt text-error">{errors.name}</span>
                  </label>
                )}
              </div>

              {/* Username */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">Username</span>
                  <div
                    className="tooltip"
                    data-tip="Your PostgreSQL username"
                  >
                    <FaInfoCircle className="w-3 h-3 text-base-content/50" />
                  </div>
                </label>
                <label className="input input-bordered flex items-center gap-2 w-full">
                  <FaUser className="w-4 h-4 text-base-content/50" />
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleInputChange("username", e.target.value)}
                    placeholder="postgres"
                    className={`grow ${errors.username ? "text-error" : ""}`}
                  />
                </label>
                {errors.username && (
                  <label className="label">
                    <span className="label-text-alt text-error">{errors.username}</span>
                  </label>
                )}
              </div>

              {/* Password */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">Password</span>
                  <div
                    className="tooltip"
                    data-tip="Re-enter your password for security"
                  >
                    <FaInfoCircle className="w-3 h-3 text-base-content/50" />
                  </div>
                </label>
                <label className="input input-bordered flex items-center gap-2 w-full">
                  <FaLock className="w-4 h-4 text-base-content/50" />
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    placeholder="Enter password (leave blank to keep current)"
                    className="grow"
                  />
                </label>
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Leave blank to keep the current password
                  </span>
                </label>
              </div>

              {/* Host */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">Host</span>
                  <div
                    className="tooltip"
                    data-tip="Database server hostname or IP address"
                  >
                    <FaInfoCircle className="w-3 h-3 text-base-content/50" />
                  </div>
                </label>
                <label className="input input-bordered flex items-center gap-2 w-full">
                  <FaServer className="w-4 h-4 text-base-content/50" />
                  <input
                    type="text"
                    value={formData.host}
                    onChange={(e) => handleInputChange("host", e.target.value)}
                    placeholder="localhost or server hostname"
                    className={`grow ${errors.host ? "text-error" : ""}`}
                  />
                </label>
                {errors.host && (
                  <label className="label">
                    <span className="label-text-alt text-error">{errors.host}</span>
                  </label>
                )}
              </div>

              {/* Port and Database in a row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Port */}
                <div className="form-control w-full">
                  <label className="label mb-2">
                    <span className="label-text font-medium">Port</span>
                  </label>
                  <label className="input input-bordered flex items-center gap-2 w-full">
                    <FaNetworkWired className="w-4 h-4 text-base-content/50" />
                    <input
                      type="number"
                      value={formData.port}
                      onChange={(e) => handleInputChange("port", e.target.value)}
                      placeholder="5432"
                      className={`grow ${errors.port ? "text-error" : ""}`}
                    />
                  </label>
                  {errors.port && (
                    <label className="label">
                      <span className="label-text-alt text-error">{errors.port}</span>
                    </label>
                  )}
                </div>

                {/* Database */}
                <div className="form-control w-full">
                  <label className="label mb-2">
                    <span className="label-text font-medium">Database</span>
                  </label>
                  <label className="input input-bordered flex items-center gap-2 w-full">
                    <FaDatabase className="w-4 h-4 text-base-content/50" />
                    <input
                      type="text"
                      value={formData.database}
                      onChange={(e) => handleInputChange("database", e.target.value)}
                      placeholder="mydb"
                      className={`grow ${errors.database ? "text-error" : ""}`}
                    />
                  </label>
                  {errors.database && (
                    <label className="label">
                      <span className="label-text-alt text-error">{errors.database}</span>
                    </label>
                  )}
                </div>
              </div>

              {/* Info Alert */}
              <div className="alert bg-base-200/50 border-0">
                <FaInfoCircle className="w-4 h-4" />
                <div className="text-sm">
                  <p className="font-medium">Updating Connection</p>
                  <p className="text-base-content/70">
                    All fields will be updated. Password must be re-entered for security.
                  </p>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="divider"></div>
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-4 text-error">
                  Danger Zone
                </h2>
                <button
                  type="button"
                  onClick={() => setDeleteModalOpen(true)}
                  className="btn btn-error btn-outline btn-sm"
                >
                  <FaTrash className="w-3 h-3 mr-2" />
                  Delete this connection
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-base-200">
                <button
                  type="button"
                  onClick={() => navigate("/connections")}
                  className="btn btn-ghost"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.name.trim()}
                  className="btn btn-primary"
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <DeleteConnectionModal
          isOpen={deleteModalOpen}
          connectionName={connection.name}
          onConfirm={handleDelete}
          onCancel={() => setDeleteModalOpen(false)}
          isDeleting={isDeleting}
        />

        {/* Toast Notification */}
        {showToast && (
          <div className="toast toast-bottom toast-center">
            <div className={`alert ${toastType === 'success' ? 'alert-success' : 'alert-error'}`}>
              {toastType === 'success' ? (
                <FaCheck className="w-4 h-4" />
              ) : (
                <FaTimes className="w-4 h-4" />
              )}
              <span>{toastMessage}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditPostgreSQLConnection;